import React from "react";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Tooltip, Typography } from "@mui/material";
import type { IntegrationRunListItem } from "features/integrationsBeta/integrationsBeta";
import ClientRunStatusChip from "./ClientRunStatusChip";

type Props = {
  run: Pick<
    IntegrationRunListItem,
    "client_visible" | "client_status" | "original_run"
  >;
  clientStatusLabel: (value: string | null | undefined) => string;
  /** Tooltip of the pill, for a run the client sees. */
  tooltip?: React.ReactNode;
};

const RERUN_HINT =
  "Re-runs are internal: the client sees the original run and its status - set it there.";
const NOT_SHOWN_HINT = "The client does not see this run.";

/**
 * The client-facing status of an INTERNAL history row (CAL-6165): the pill
 * exactly as the client sees it - a status set by hand looks like any other,
 * who set it and why is in the tooltip - or, for a run the client never sees
 * (a re-run), a muted "Not shown to client" saying why. Client-facing surfaces
 * render ClientRunStatusChip directly and never this.
 */
export default function RunClientStatus({
  run,
  clientStatusLabel,
  tooltip,
}: Props) {
  if (!run.client_visible) {
    return (
      <Tooltip
        title={run.original_run !== null ? RERUN_HINT : NOT_SHOWN_HINT}
        disableInteractive
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            whiteSpace: "nowrap",
          }}
        >
          <VisibilityOffOutlinedIcon sx={{ fontSize: 14 }} />
          Not shown to client
        </Typography>
      </Tooltip>
    );
  }
  return (
    <ClientRunStatusChip
      status={run.client_status}
      label={clientStatusLabel(run.client_status)}
      tooltip={tooltip}
    />
  );
}
